import { IncomingHttpHeaders } from 'http';
import getConfig from 'next/config';
import type { AxiosResponse } from 'axios';

import { donedealGateway } from '../api/api-utils/commonApiUtils';
import {
  AxiosResponseWithTimer,
  withDuration,
  withMinDuration,
} from '../api/api-utils/middleware';

import type {
  ISavedSearch,
  IPreCheckResponse,
  ISearchRequest,
} from '../api/types/savedSearchApiTypes';
import type { TCreateSavedSearchFromAd } from '../api/types/searchApiTypes';
import type { SavedSearchQuery, SavedSearch } from '../types';

async function createSavedSearch(
  data: SavedSearchQuery,
  minTime: number,
  headers: IncomingHttpHeaders,
  timeout?: number,
): Promise<AxiosResponseWithTimer<SavedSearchQuery>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponseWithTimer<SavedSearch> = await withMinDuration(
    withDuration(donedealGateway),
    minTime,
  ).post(
    `/ddapi/v1/users/savedsearches`,
    {
      ...data,
      startTime: new Date().getTime(),
    },
    {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: timeout || parseInt(API_LISTINGS_TIMEOUT),
    },
  );
  return response;
}

const createSavedSearchFromAd: TCreateSavedSearchFromAd<
  Promise<AxiosResponseWithTimer<ISavedSearch>>
> = async (data, adId, minTime, headers) => {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponseWithTimer<ISavedSearch> = await withMinDuration(
    withDuration(donedealGateway),
    minTime,
  ).post(`/ddapi/v1/users/savedsearches/ad/${adId}`, data, {
    headers: {
      accept: 'application/json',
      ...headers,
    },
    timeout: parseInt(API_LISTINGS_TIMEOUT),
  });
  return response;
};

async function getSavedSearches(
  args: {
    page: number;
    offset: number;
  },
  headers: IncomingHttpHeaders,
): Promise<AxiosResponse<ISavedSearch[]>> {
  const { page, offset } = args;
  const response: AxiosResponse<ISavedSearch[]> = await donedealGateway.get(
    `/ddapi/v1/users/savedsearches?page=${page}&size=${offset}`,
    {
      headers,
    },
  );
  return response;
}

async function deleteSavedSearch(
  id: number,
  minTime: number,
  headers: IncomingHttpHeaders,
): Promise<AxiosResponseWithTimer<string>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponseWithTimer<string> = await withMinDuration(
    withDuration(donedealGateway),
    minTime,
  ).delete(`/ddapi/v1/users/savedsearches/${id}`, {
    headers,
    timeout: parseInt(API_LISTINGS_TIMEOUT),
  });
  return response;
}

async function updateSearchAlert(
  adId: number,
  payload: Pick<ISavedSearch, 'name' | 'description' | 'frequency' | 'search'>,
  minTime: number,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
): Promise<AxiosResponseWithTimer<null>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponseWithTimer<null> = await withMinDuration(
    withDuration(donedealGateway),
    minTime,
  ).put(`/ddapi/v1/users/savedsearches/${adId}`, payload, {
    headers: {
      ...headers,
      timeout: parseInt(API_LISTINGS_TIMEOUT),
    },
  });
  return response;
}

async function searchPagePreCheck(
  payload: ISearchRequest,
  headers: IncomingHttpHeaders,
  timeout?: number,
): Promise<AxiosResponse<IPreCheckResponse>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IPreCheckResponse> = await donedealGateway.post(
    `/ddapi/v1/users/savedsearches/precheck`,
    {
      ...payload,
      startTime: new Date().getTime(),
    },
    {
      headers,
      timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
    },
  );
  return response;
}

async function adDetailsPagePreCheck(
  adId: number,
  headers: IncomingHttpHeaders,
): Promise<AxiosResponse<IPreCheckResponse>> {
  const response: AxiosResponse<IPreCheckResponse> = await donedealGateway.post(
    `/ddapi/v1/users/savedsearches/ad/${adId}/precheck`,
    null,
    {
      headers,
    },
  );
  return response;
}

export const savedSearchApi = {
  createSavedSearch,
  createSavedSearchFromAd,
  deleteSavedSearch,
  getSavedSearches,
  updateSearchAlert,
  searchPagePreCheck,
  adDetailsPagePreCheck,
};
